import currency from 'currency.js';
import { isNil } from 'lodash';

/**
 * The USD (US Dollar) class is a simple wrapper around currency.js to enforce usage of cent-based values. Only USD
 * is supported. All arithmetic operations should use cent-based values.
 *
 * See currency.js: https://currency.js.org/
 *
 * @param value
 * @example
 * const money = USD.fromCents(100).add(50);
 * money.string;   // returns "$1.50"
 * money.dollars  // returns 1.5
 * money.cents;   // returns 150
 */
export class USD {
  private currency: currency;
  private precision = 2;
  private p = 1;

  private constructor(value: number, precision = 2) {
    this.precision = precision;
    this.p = Math.pow(10, precision - 2);
    this.currency = currency(value * this.p, { fromCents: true, precision });
  }

  /**
   * Create a USD instance from cents.
   *
   * @example
   * const money = USD.fromCents(100);
   * money.string;   // returns "$1.00"
   */
  public static fromCents(valueInCents: number, precision = 2) {
    return new USD(valueInCents, precision);
  }

  public static fromSerialized(value: any): USD | null {
    if (isNil(value)) return null;

    if ('precision' in value && 'p' in value && 'currency' in value) {
      if (typeof value.precision === 'number' && typeof value.p === 'number' && typeof value.currency === 'number') {
        // Inverse of the equation applied in the constructor...
        const inputPrecision = Math.log10(value.p) + 2;
        // foo.currency is a float value representing _cents_ so 1234.56 = $12.3456
        const scalar = 100;
        return USD.fromCents(value.currency * scalar, inputPrecision);
      }
    }

    return null;
  }

  /**
   * Create a USD instance from dollars.
   *
   * @example
   * const money = USD.fromDollars(1);
   * money.string;   // returns "$1.00"
   */
  public static fromDollars(valueInDollars: number, precision = 2) {
    return new USD(valueInDollars * 100, precision);
  }

  /**
   * Outputs USD in dollars.
   *
   * @example
   * const money = USD.fromCents(100)
   * const dollars = money.dollars // returns 1
   */
  get dollars(): number {
    return this.currency.value;
  }

  /**
   * Outputs USD in cents.
   *
   * @example
   * const money = USD.fromDollar(100)
   * const cents = money.cents // returns 10000
   */
  get cents(): number {
    return this.currency.intValue / this.p;
  }

  /**
   * Outputs USD in a human-friendly string.
   *
   * @example
   * const money = USD.fromDollar(100)
   * const string = money.string // returns "$1.00"
   */
  get string(): string {
    return this.currency.format();
  }

  get serial() {
    return { cents: this.cents, precision: this.precision };
  }

  public static getAmountAsCents(value: number | USD) {
    return value instanceof USD ? value.cents : value;
  }

  public add(amountInCents: number | USD): USD {
    const result = this.currency.add(USD.getAmountAsCents(amountInCents));
    return new USD(result.intValue);
  }

  public subtract(amountInCents: number | USD): USD {
    const result = this.currency.subtract(USD.getAmountAsCents(amountInCents));
    return new USD(result.intValue);
  }

  public multiply(value: number): USD {
    const result = this.currency.multiply(value);
    return new USD(result.intValue);
  }

  public divide(value: number): USD {
    const result = this.currency.divide(value);
    return new USD(result.intValue);
  }

  public distribute(numParts: number): USD[] {
    const distrubutions = this.currency.distribute(numParts);
    return distrubutions.map((distribution) => new USD(distribution.intValue));
  }
}
